import '@cwah5/startersdk';
import { hasFeatureCanary, WorkspaceConfiguration } from '@citrite/workspace-ui-platform';
import { logError } from 'remoteLogging';
import { FeatureFlag } from 'Environment/FeatureFlag';

type InitResult = { gacsSupport: 'true' | 'false' };

const STARTER_SDK_FEATURE_TAG = { tags: { feature: 'CWAStarterSDK' } };
const UNSUPPORTED: InitResult = { gacsSupport: 'false' };

let initPromise: Promise<InitResult> | null = null;

export function initHtml5SharedSDK(): Promise<InitResult> {
	if (initPromise) {
		return initPromise;
	}
	initPromise = (async () => {
		try {
			const result = (await CWAStarterSDK.init()) as InitResult;
			return result?.gacsSupport === 'true' ? result : UNSUPPORTED;
		} catch (error) {
			logError(error, STARTER_SDK_FEATURE_TAG);
			return UNSUPPORTED;
		}
	})();
	return initPromise;
}

export async function fetchSettingsViaStarterSdk(token: string): Promise<void> {
	const { gacsSupport } = await initHtml5SharedSDK();
	if (gacsSupport !== 'true') {
		return;
	}
	try {
		await CWAStarterSDK.fetchSettings(token);
	} catch (error) {
		logError(error, STARTER_SDK_FEATURE_TAG);
	}
}

/**
 * Delivers a GACS access token to the HTML5 Starter SDK (SharedWorker) when it can
 * handle delivery, reporting whether it did.
 *
 * Returns `true` when the SharedWorker took ownership of the token — i.e. the
 * `EnableHTML5StarterSDK` feature flag is on AND the SDK initialised with SharedWorker
 * support (`init()` reported `gacsSupport: 'true'`). Returns `false` when the caller
 * should fall back to the legacy postMessage delivery (flag off, or no SharedWorker in
 * this environment — e.g. WKWebView / mobile).
 *
 * The token is supplied by the caller (reusing the one already fetched for app-config
 * delivery), so no additional token fetch is performed here.
 */
export async function tryDeliverTokenViaStarterSdk(
	token: string,
	workspaceConfiguration: WorkspaceConfiguration
): Promise<boolean> {
	if (!hasFeatureCanary(workspaceConfiguration, FeatureFlag.EnableHTML5StarterSDK)) {
		return false;
	}
	const { gacsSupport } = await initHtml5SharedSDK();
	if (gacsSupport !== 'true') {
		return false;
	}
	await fetchSettingsViaStarterSdk(token);
	return true;
}
